import Image from '@/components/Image';
import { LandingPagePathsQuery, SemLandingPageQuery, SemLandingPageQueryVariables } from '@/graphql';
import LandingPagePathsQueryDocument from '@/graphql/queries/LandingPagePaths.gql';
import SEMLandingPageQueryDocument from '@/graphql/queries/SEMLandingPage.gql';
import ModularPageLayout from '@/layouts/ModularPageLayout';
import CraftClient from '@/lib/graphql';
import { getStaticPagePaths, getStaticPageProps } from '@/lib/next';
import { getSingle, notEmpty } from '@/lib/typescript-helpers';
import { ModularPageProps, parseModuleBuilderProps } from '@/modules/ModuleBuilder';
import LandingPageTemplate from '@/templates/LandingPageTemplate';
import { cloneDeep } from 'lodash';
import { FC, ReactNode, useMemo } from 'react';

const LandingPage: PageTemplate<ModularPageProps> = ({ modularPageProps }) => {
  const moduleData = useMemo(() => {
    const props = cloneDeep(modularPageProps);

    if (!props.entry?.moduleBuilder) return null;

    props.entry.moduleBuilder = props.entry.moduleBuilder
      .filter(notEmpty)
      .map((_module) => {
        if (!('module' in _module) || !_module.module[0]) return null;
        return _module;
      })
      .filter(notEmpty);

    return props;
  }, [modularPageProps]);

  if (!moduleData) return null;

  return <ModularPageLayout props={moduleData} />;
};

LandingPage.template = (({ entry, children }) => {
  let additionalLockupLogo: ReactNode = null;

  const lockupLogo = useMemo(() => {
    const logo = getSingle(getSingle(entry?.logo)?.lockupLogo);
    const customerLogo = getSingle(getSingle(entry?.logo)?.customerLogo);

    return logo ?? customerLogo;
  }, [entry]);

  if (entry?.__typename === 'landingPages_sem_Entry' && lockupLogo) {
    additionalLockupLogo = (
      <Image src={lockupLogo?.url!} width={lockupLogo?.width!} height={lockupLogo?.height!} alt="" />
    );
  }

  return <LandingPageTemplate additionalLockupLogo={additionalLockupLogo}>{children}</LandingPageTemplate>;
}) satisfies FC<PageTemplateProps<SemLandingPageQuery>>;

export default LandingPage;

export const getStaticPaths = getStaticPagePaths('lp', async (context) => {
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }
  const client = new CraftClient();

  const data = await client.request<LandingPagePathsQuery>(LandingPagePathsQueryDocument);

  const paths =
    data.entries?.map((path) => ({
      params: {
        slug: path?.slug!,
      },
    })) || [];

  return {
    paths,
    fallback: 'blocking',
  };
});

export const getStaticProps = getStaticPageProps<{}, { slug: string }>(
  (params) => `lp/${params?.slug}`,
  async ({ client, context: { params } }) => {
    const props = await client.request<SemLandingPageQuery, SemLandingPageQueryVariables>(SEMLandingPageQueryDocument, {
      slug: params?.slug,
    });

    if (!props.entry) {
      return { notFound: true };
    }

    return {
      props: parseModuleBuilderProps(props),
    };
  },
);
